import * as React from "react";

import { DatasheetFieldComponentGetDto } from "@common/dto/datasheet/field-component/get-dto";
import { IDatasheetResultElement } from "@modules/datasheets/components/result/types/datasheet-element";
import { getCommonFieldBasedElementParams, useDatasheetElementController } from "@modules/datasheets/components/result/controllers/datasheet-element";

export type DropdownFieldProps = IDatasheetResultElement<DatasheetFieldComponentGetDto>;

export const useDropdownFieldController = (props: DropdownFieldProps) => {
  const { datasheetElement, onChange } = props;

  const { 
    headingSizeDependencies, 
    headingPlacedHorizontally, 
    headingPosition 
  } = getCommonFieldBasedElementParams(props);

  const baseDatasheetElementController = useDatasheetElementController(
    props,
    { headingSizeDependencies }
  );

  const getCurrentValue = () => (datasheetElement.original.field as any).value ?? '';
  const [selectedValue, setSelectedValue] = React.useState<string | number>(getCurrentValue());

  React.useEffect(() => {
    const newValue = getCurrentValue();
    setSelectedValue(prev => prev !== newValue ? newValue : prev);
  }, [datasheetElement.original.field]);

  const handleFieldValueSave = React.useCallback((id: string | number) => {
    setSelectedValue(id);
    onChange(datasheetElement.id, {
      ...datasheetElement,
      original: {
        ...datasheetElement.original,
        field: {
          ...datasheetElement.original.field,
          value: id,
        },
      },
    });
  }, [onChange, datasheetElement]);

  // Get current value from merged data
  const currentValue = (datasheetElement.original as any).dataValue ?? (datasheetElement.original as any).displayValue;

  const normalizeDropdownOptions = React.useMemo((): Array<{ label: string; value: string; color?: string; }> => {
    const dropdownValues = (datasheetElement.original.field as any).dropdownValues;
    if (dropdownValues && Array.isArray(dropdownValues) && dropdownValues.length > 0) {
      return dropdownValues.map((value: string) => ({ value, label: value }));
    }

    const options = (datasheetElement.original.field as any).options;
    if (options?.length) {
      return options.map((option: any) => ({
        value: option.actualValue ?? option.value,
        label: option.displayValue ?? option.label ?? option.actualValue ?? option.value,
        color: option.color ?? option.optionColor ?? undefined
      }));
    }

    return [];
  }, [datasheetElement.original.field]);

  return {
    handleFieldValueSave,
    selectedValue,
    headingPosition,
    headingPlacedHorizontally,
    normalizeDropdownOptions,
    currentValue,
    ...baseDatasheetElementController,
  };
};